import { computed, ref } from 'vue';
import jsYaml from 'js-yaml';
import {
  getLatestDraftOrSaved,
  getLatestSaved
} from '../../lib/callModContent';
import {
  commitDraft,
  publishCourse,
  updateDraft
} from '../lib/callModContentAuthors';
import {
  extractAudioAiJitConfig,
  getJitEnabledLocalesFromConfig,
  type CourseAudioJitConfig
} from '../../lib/audioJitCourseConfig';
import {
  getCustomJitOverrideLocales,
  getTier2LocalesFromYaml,
  isFullCustomisationYaml,
  planStripLegacyAudioPaths,
  type StrippableLocale
} from '../../lib/stripLegacyAudioPaths';
import {
  courseKey,
  type DiscoveryCourseRow,
  useBulkCustomisationDiscovery
} from './useBulkCustomisationDiscovery';

export type CleanupRowStatus =
  | 'pending'
  | 'processing'
  | 'success'
  | 'error'
  | 'skipped';

export type CleanupRow = DiscoveryCourseRow & {
  tier2Locales: string[];
  jitOverrideLocales: string[];
  strippableLocales: StrippableLocale[];
  /** True when the customisation embeds the full course (top-level `pages`). */
  isFullCustomisation: boolean;
  status: CleanupRowStatus;
  error?: string;
};

function baseCourseHasJit(config: CourseAudioJitConfig | undefined): boolean {
  if (!config) return false;
  if (config === true) return true;
  if (typeof config === 'object' && !Array.isArray(config)) {
    if (
      'provider' in config ||
      'model' in config ||
      'voice' in config ||
      'locale' in config
    ) {
      return true;
    }
    return Object.keys(config).length > 0;
  }
  return false;
}

const COMMIT_MESSAGE =
  'Remove legacy S3 audio paths for JiT locales (bulk cleanup)';

/** Same inclusion rule as Tier-2 bulk audio: customisations with S3 audio paths only. */
function customisationHasTier2Audio(course: DiscoveryCourseRow): boolean {
  return course.hasAudio && getTier2LocalesFromYaml(course.yaml).length > 0;
}

export function useJitAudioCleanup() {
  const discovery = useBulkCustomisationDiscovery();
  const baseJitConfigs = ref<Map<number, CourseAudioJitConfig | undefined>>(
    new Map()
  );
  const rowStatuses = ref<Map<string, CleanupRowStatus>>(new Map());
  const rowErrors = ref<Map<string, string>>(new Map());
  const isProcessing = ref(false);
  const shouldStopProcessing = ref(false);
  const currentProcessing = ref('');
  const completedCount = ref(0);
  const totalCount = ref(0);
  const publishDestination = ref<'test' | 'production'>('production');
  /** Full-course customisations (top-level `pages`) are excluded from the table
   *  unless opted in — mirrors the "include duplicates" filter. */
  const includeFullCustomisations = ref(false);
  const concurrencyLimit = 3;

  const cleanupRows = computed<CleanupRow[]>(() => {
    const rows = discovery.flattenCustomisations(customisationHasTier2Audio);

    return rows
      .map(course => {
        const key = courseKey(course.firmCode, course.courseId);
        const baseJitConfig = baseJitConfigs.value.get(course.baseCourseId);
        const plan = planStripLegacyAudioPaths(course.yaml, baseJitConfig);
        const storedStatus = rowStatuses.value.get(key);

        return {
          ...course,
          tier2Locales: getTier2LocalesFromYaml(course.yaml),
          jitOverrideLocales: getCustomJitOverrideLocales(course.yaml),
          strippableLocales: plan.strippableLocales,
          isFullCustomisation: isFullCustomisationYaml(course.yaml),
          status:
            storedStatus ??
            (plan.strippableLocales.length === 0 ? 'skipped' : 'pending'),
          error: rowErrors.value.get(key)
        };
      })
      .filter(
        row => includeFullCustomisations.value || !row.isFullCustomisation
      );
  });

  const baseJitSummaries = computed(() => {
    const summaries: {
      courseId: number;
      jitLocales: string[];
      hasJit: boolean;
      error?: string;
    }[] = [];

    const loadedBaseIds = new Set([
      ...discovery.customisationsReports.value.keys(),
      ...discovery.baseCourseLoadErrors.value.keys()
    ]);

    loadedBaseIds.forEach(baseCourseId => {
      const config = baseJitConfigs.value.get(baseCourseId);
      const error = discovery.baseCourseLoadErrors.value.get(baseCourseId);
      const candidateLocales = [
        ...new Set(
          cleanupRows.value
            .filter(row => row.baseCourseId === baseCourseId)
            .flatMap(row => row.tier2Locales)
        )
      ];

      const jitLocales =
        config === true
          ? ['all locales']
          : getJitEnabledLocalesFromConfig(config, candidateLocales);

      summaries.push({
        courseId: baseCourseId,
        jitLocales,
        hasJit: baseCourseHasJit(config),
        error
      });
    });

    return summaries;
  });

  const basesWithoutJit = computed(() =>
    baseJitSummaries.value.filter(summary => !summary.hasJit && !summary.error)
  );

  const cleanupSummary = computed(() => {
    const { totalBaseCourses, totalCustomisations } =
      discovery.totalSummary.value;
    const withAudio = cleanupRows.value.length;
    const withStrippable = cleanupRows.value.filter(
      row => row.strippableLocales.length > 0
    ).length;

    return { totalBaseCourses, totalCustomisations, withAudio, withStrippable };
  });

  const selectedCleanupRows = computed(() =>
    cleanupRows.value.filter(row => discovery.isCourseSelected(row))
  );

  const selectedStrippableCount = computed(
    () =>
      selectedCleanupRows.value.filter(row => row.strippableLocales.length > 0)
        .length
  );

  const hasAnyUnsavedChanges = discovery.hasAnyUnsavedChanges(cleanupRows);
  const unsavedChangesCount = discovery.unsavedChangesCount(cleanupRows);

  const failedRows = computed(() =>
    cleanupRows.value.filter(row => row.status === 'error')
  );

  const processingCompleted = computed(() => {
    const strippableSelected = selectedCleanupRows.value.filter(
      row => row.strippableLocales.length > 0
    );
    if (strippableSelected.length === 0) return false;

    return strippableSelected.every(row => {
      const key = courseKey(row.firmCode, row.courseId);
      const status = rowStatuses.value.get(key);
      return status === 'success' || status === 'skipped';
    });
  });

  const loadBaseJitConfigs = async () => {
    baseJitConfigs.value.clear();

    for (const baseCourseId of discovery.customisationsReports.value.keys()) {
      try {
        const baseCourse = await getLatestSaved({ courseId: baseCourseId });
        if (!baseCourse?.yaml) {
          baseJitConfigs.value.set(baseCourseId, undefined);
          continue;
        }
        const parsed = jsYaml.load(baseCourse.yaml) as Record<string, unknown>;
        baseJitConfigs.value.set(baseCourseId, extractAudioAiJitConfig(parsed));
      } catch {
        baseJitConfigs.value.set(baseCourseId, undefined);
      }
    }
  };

  const loadCustomisations = async () => {
    rowStatuses.value.clear();
    rowErrors.value.clear();
    await discovery.loadCustomisations();
    await loadBaseJitConfigs();

    discovery.selectedCourses.value.clear();
    cleanupRows.value.forEach(row => {
      if (row.strippableLocales.length > 0) {
        discovery.selectedCourses.value.add(
          courseKey(row.firmCode, row.courseId)
        );
      }
    });

    await discovery.checkAllUnsavedChanges(cleanupRows.value);
  };

  const resetProcessingState = () => {
    rowStatuses.value.clear();
    rowErrors.value.clear();
    isProcessing.value = false;
    shouldStopProcessing.value = false;
    currentProcessing.value = '';
    completedCount.value = 0;
    totalCount.value = 0;
    discovery.clearDiscovery();
    baseJitConfigs.value.clear();
  };

  const publishLive = (draft: CourseDraft, publishData: Partial<PublishBody>) =>
    Promise.all([
      publishCourse({ draft, publishData, publishTo: 'production' }),
      publishCourse({ draft, publishData, publishTo: 'test' })
    ]);

  const publishInternal = (
    draft: CourseDraft,
    publishData: Partial<PublishBody>
  ) => publishCourse({ draft, publishData, publishTo: 'test' });

  const processSingleRow = async (row: CleanupRow) => {
    const key = courseKey(row.firmCode, row.courseId);
    currentProcessing.value = key;
    rowStatuses.value.set(key, 'processing');
    rowErrors.value.delete(key);

    try {
      if (shouldStopProcessing.value) return;

      const baseJitConfig = baseJitConfigs.value.get(row.baseCourseId);
      const latest = await getLatestDraftOrSaved({
        courseId: row.courseId,
        firmCode: row.firmCode
      });

      if (!latest?.yaml) {
        throw new Error('No draft or saved content found');
      }

      const plan = planStripLegacyAudioPaths(latest.yaml, baseJitConfig);
      if (plan.unchanged) {
        rowStatuses.value.set(key, 'skipped');
        return;
      }

      const updatedDraft: CourseDraft = {
        ...latest,
        yaml: plan.updatedYaml
      };

      await updateDraft(updatedDraft);
      await commitDraft(
        { draft: updatedDraft, commitMessage: COMMIT_MESSAGE },
        { skipUpdateDraft: true }
      );

      if (shouldStopProcessing.value) return;

      const publishData: Partial<PublishBody> = {
        level: 'minor',
        message: '',
        internal: 'Bulk JiT audio path cleanup'
      };

      const publish =
        publishDestination.value === 'production'
          ? publishLive
          : publishInternal;

      await publish(updatedDraft, publishData);

      rowStatuses.value.set(key, 'success');
    } catch (error) {
      rowStatuses.value.set(key, 'error');
      rowErrors.value.set(
        key,
        error instanceof Error ? error.message : 'Unknown error'
      );
    } finally {
      completedCount.value++;
    }
  };

  const processWithConcurrency = async (rows: CleanupRow[]) => {
    const queue = [...rows];

    const worker = async () => {
      while (queue.length > 0 && !shouldStopProcessing.value) {
        const row = queue.shift();
        if (row) await processSingleRow(row);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(concurrencyLimit, rows.length) }, () =>
        worker()
      )
    );
  };

  const startCleanup = async () => {
    const rows = selectedCleanupRows.value.filter(
      row => row.strippableLocales.length > 0
    );
    if (rows.length === 0) return;

    isProcessing.value = true;
    shouldStopProcessing.value = false;
    completedCount.value = 0;
    totalCount.value = rows.length;

    rows.forEach(row => {
      const key = courseKey(row.firmCode, row.courseId);
      rowStatuses.value.set(key, 'pending');
      rowErrors.value.delete(key);
    });

    await processWithConcurrency(rows);

    isProcessing.value = false;
    shouldStopProcessing.value = false;
    currentProcessing.value = '';
  };

  const retryFailed = async () => {
    const rows = failedRows.value.filter(row =>
      discovery.isCourseSelected(row)
    );
    if (rows.length === 0) return;

    isProcessing.value = true;
    shouldStopProcessing.value = false;
    completedCount.value = 0;
    totalCount.value = rows.length;

    rows.forEach(row => {
      const key = courseKey(row.firmCode, row.courseId);
      rowStatuses.value.set(key, 'pending');
      rowErrors.value.delete(key);
    });

    await processWithConcurrency(rows);

    isProcessing.value = false;
    shouldStopProcessing.value = false;
    currentProcessing.value = '';
  };

  const stopProcessing = () => {
    shouldStopProcessing.value = true;
  };

  return {
    ...discovery,
    baseJitConfigs,
    cleanupRows,
    includeFullCustomisations,
    baseJitSummaries,
    basesWithoutJit,
    cleanupSummary,
    selectedCleanupRows,
    selectedStrippableCount,
    hasAnyUnsavedChanges,
    unsavedChangesCount,
    failedRows,
    processingCompleted,
    isProcessing,
    shouldStopProcessing,
    currentProcessing,
    completedCount,
    totalCount,
    publishDestination,
    loadCustomisations,
    resetProcessingState,
    startCleanup,
    retryFailed,
    stopProcessing
  };
}
